import React, { type ReactElement } from 'react'
import { type SupplierSettingsTabProps } from '../hooks/useSupplierTabs'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import styles from '../assets/scss/Suppliers.module.scss'
import loaderStyles from '../../common/assets/scss/LoadingSpinner.module.scss'
import LoadingSpinner from '../../common/components/LoadingSpinner'
import { Col, Form, Row } from 'react-bootstrap'
import { handleAsync } from '../../common/utils/asyncHandler'
import classNames from 'classnames'
import MaPanel from '../../common/components/MaPanel'
import { useFormDisabled } from '../../common/components/FormDisabledContext'
import MaNavBar from '../../common/components/MaNavBar'

function AdvancedSettings ({
  isLoading,
  hasLoadingError,
  supplierSettingsData,
  onSubmit,
  onDirtyChange
}: SupplierSettingsTabProps): ReactElement {
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    values: supplierSettingsData,
    defaultValues: supplierSettingsData
  })

  const isDisabled = useFormDisabled()

  React.useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  React.useEffect(() => {
    if (supplierSettingsData) {
      reset(supplierSettingsData)
    }
  }, [supplierSettingsData, reset])

  return (
    <div className={styles.supplierDataTab}>
      <MaNavBar
        onSubmit={handleAsync(handleSubmit(onSubmit))}
        formDirty={isDirty}
      />
      {(isLoading || hasLoadingError) && (
        <div className="flex items-center justify-center h-full">
          <div className={loaderStyles.spinner}>
            <LoadingSpinner />
          </div>
        </div>
      )}

      <MaPanel
        className={classNames(
          isDisabled ? styles['disabled-panel'] : '',
          'pt-2'
        )}
      >
        <MaPanel.Body>
          {!isLoading && (
            <>
              <div className={styles['section-title-panel']}>
                <h4 className={styles['section-title']}>
                  {t('suppliers.advancedSettings.outbound_settings')}
                </h4>
              </div>
              <Form onSubmit={handleAsync(handleSubmit(onSubmit))}>
                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.orderIntegrationType'
                        )}
                      </Form.Label>
                      <Form.Select
                        {...register('orderIntegrationType', {
                          disabled: isDisabled
                        })}
                        isInvalid={!!errors.orderIntegrationType}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.orderIntegrationType'
                        )}
                      >
                        <option>{t('suppliers.label.pleaseSelect')}</option>
                        <option value={0}>
                          {t(
                            'suppliers.advancedSettings.tab.options.orderIntegrationType1'
                          )}
                        </option>
                        <option value={3}>
                          {t(
                            'suppliers.advancedSettings.tab.options.orderIntegrationType2'
                          )}
                        </option>
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.orderIntegrationType?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlToCredentialDomain'
                        )}
                      </Form.Label>
                      <Form.Select
                        {...register('cxmlToCredentialDomain', {
                          disabled: isDisabled,
                          required: {
                            value: true,
                            message: t('validation.error.mandatory', {
                              fieldName: t(
                                'suppliers.advancedSettings.tab.label.cxmlToCredentialDomain'
                              )
                            })
                          }
                        })}
                        defaultValue={
                          supplierSettingsData?.cxmlToCredentialDomain ?? ''
                        }
                        isInvalid={!!errors.cxmlToCredentialDomain}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.cxmlToCredentialDomain'
                        )}
                      >
                        <option value="">
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        <option value={1}>
                          {t(
                            'suppliers.advancedSettings.tab.options.credentialDomain1'
                          )}
                        </option>
                        <option value={2}>
                          {t(
                            'suppliers.advancedSettings.tab.options.credentialDomain2'
                          )}
                        </option>
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.cxmlToCredentialDomain?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('isOrderSendingActive', {
                          disabled: isDisabled
                        })}
                        id={'cbIsOrderSendingActive'}
                        isInvalid={!!errors.isOrderSendingActive}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.isOrderSendingActive'
                        )}
                      />

                      <Form.Label
                        htmlFor={'cbIsOrderSendingActive'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.advancedSettings.tab.label.isOrderSendingActive'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>
                </Row>

                <br />
                <br />
                <h4 className={styles['section-title']}>
                  {t('suppliers.advancedSettings.cxml')}
                </h4>
                <hr />

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlToIdentity'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('cxmlToIdentity', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 255,
                            message: t('validation.error.string.max_length', {
                              count: 255
                            })
                          }
                        })}
                        isInvalid={!!errors.cxmlToIdentity}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.cxmlToIdentity'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.cxmlToIdentity?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlToPassword'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('cxmlToPassword', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 255,
                            message: t('validation.error.string.max_length', {
                              count: 255
                            })
                          }
                        })}
                        isInvalid={!!errors.cxmlToPassword}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.cxmlToPassword'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.cxmlToPassword?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlFromIdentity'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('cxmlFromIdentity', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 50,
                            message: t('validation.error.string.max_length', {
                              count: 50
                            })
                          }
                        })}
                        isInvalid={!!errors.cxmlFromIdentity}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.cxmlFromIdentity'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.cxmlFromIdentity?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <br />
                <br />
                <h4 className={styles['section-title']}>
                  {t('suppliers.advancedSettings.basic_auth_settings')}
                </h4>
                <hr />

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.basicAuthUsername'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('basicAuthUsername', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 50,
                            message: t('validation.error.string.max_length', {
                              count: 50
                            })
                          }
                        })}
                        isInvalid={!!errors.basicAuthUsername}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.basicAuthUsername'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.basicAuthUsername?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.basicAuthPassword'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('basicAuthPassword', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 50,
                            message: t('validation.error.string.max_length', {
                              count: 50
                            })
                          }
                        })}
                        isInvalid={!!errors.basicAuthPassword}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.basicAuthPassword'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.basicAuthPassword?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <br />
                <br />
                <h4 className={styles['section-title']}>
                  {t('suppliers.advancedSettings.order_settings')}
                </h4>
                <hr />

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlOrderheaderCommentSummary'
                        )}
                      </Form.Label>
                      <Form.Select
                        {...register('cxmlOrderheaderCommentSummary', {
                          disabled: isDisabled,
                          required: {
                            value: true,
                            message: t('validation.error.mandatory', {
                              fieldName: t(
                                'suppliers.advancedSettings.tab.label.cxmlOrderheaderCommentSummary'
                              )
                            })
                          }
                        })}
                        defaultValue={
                          supplierSettingsData?.cxmlOrderheaderCommentSummary ??
                          ''
                        }
                        isInvalid={!!errors.cxmlOrderheaderCommentSummary}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.cxmlOrderheaderCommentSummary'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        <option value={0}>
                          {t(
                            'suppliers.advancedSettings.tab.options.orderHeaderContentSum1'
                          )}
                        </option>
                        <option value={1}>
                          {t(
                            'suppliers.advancedSettings.tab.options.orderHeaderContentSum2'
                          )}
                        </option>
                        <option value={2}>
                          {t(
                            'suppliers.advancedSettings.tab.options.orderHeaderContentSum3'
                          )}
                        </option>
                        <option value={3}>
                          {t(
                            'suppliers.advancedSettings.tab.options.orderHeaderContentSum4'
                          )}
                        </option>
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.cxmlOrderheaderCommentSummary?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlIncludeFullAddress'
                        )}
                      </Form.Label>
                      <Form.Select
                        {...register('cxmlIncludeFullAddress', {
                          disabled: isDisabled,
                          required: {
                            value: true,
                            message: t('validation.error.mandatory', {
                              fieldName: t(
                                'suppliers.advancedSettings.tab.label.cxmlIncludeFullAddress'
                              )
                            })
                          }
                        })}
                        defaultValue={
                          supplierSettingsData?.cxmlIncludeFullAddress ?? ''
                        }
                        isInvalid={!!errors.cxmlIncludeFullAddress}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.cxmlIncludeFullAddress'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        <option value={0}>
                          {t(
                            'suppliers.advancedSettings.tab.options.addressIncludeMode1'
                          )}
                        </option>
                        <option value={1}>
                          {t(
                            'suppliers.advancedSettings.tab.options.addressIncludeMode2'
                          )}
                        </option>
                        <option value={2}>
                          {t(
                            'suppliers.advancedSettings.tab.options.addressIncludeMode3'
                          )}
                        </option>
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.cxmlIncludeFullAddress?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.advancedSettings.tab.label.cxmlEncoding')}
                      </Form.Label>
                      <Form.Select
                        {...register('cxmlEncoding', {
                          disabled: isDisabled,
                          required: {
                            value: true,
                            message: t('validation.error.mandatory', {
                              fieldName: t(
                                'suppliers.advancedSettings.tab.label.cxmlEncoding'
                              )
                            })
                          }
                        })}
                        defaultValue={supplierSettingsData?.cxmlEncoding ?? ''}
                        isInvalid={!!errors.cxmlEncoding}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.cxmlEncoding'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        <option value={1}>
                          {t(
                            'suppliers.advancedSettings.tab.options.EncodingMode1'
                          )}
                        </option>
                        <option value={2}>
                          {t(
                            'suppliers.advancedSettings.tab.options.EncodingMode2'
                          )}
                        </option>
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.cxmlEncoding?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlStateOrCounty'
                        )}
                      </Form.Label>
                      <Form.Select
                        {...register('cxmlStateOrCounty', {
                          disabled: isDisabled,
                          required: {
                            value: true,
                            message: t('validation.error.mandatory', {
                              fieldName: t(
                                'suppliers.advancedSettings.tab.label.cxmlStateOrCounty'
                              )
                            })
                          }
                        })}
                        defaultValue={
                          supplierSettingsData?.cxmlStateOrCounty ?? ''
                        }
                        isInvalid={!!errors.cxmlStateOrCounty}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.cxmlStateOrCounty'
                        )}
                      >
                        <option value={''}>
                          {t('suppliers.label.pleaseSelect')}
                        </option>
                        <option value={1}>
                          {t(
                            'suppliers.advancedSettings.tab.options.CountyOrState1'
                          )}
                        </option>
                        <option value={2}>
                          {t(
                            'suppliers.advancedSettings.tab.options.CountyOrState2'
                          )}
                        </option>
                      </Form.Select>

                      <Form.Control.Feedback type="invalid">
                        {errors?.cxmlStateOrCounty?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.quoteNumberFilter'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('quoteNumberFilter', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 255,
                            message: t('validation.error.string.max_length', {
                              count: 255
                            })
                          }
                        })}
                        isInvalid={!!errors.quoteNumberFilter}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.quoteNumberFilter'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.quoteNumberFilter?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('cxmlIncludeUnitOfMeasure', {
                          disabled: isDisabled
                        })}
                        id={'cxmlIncludeUnitOfMeasure'}
                        isInvalid={!!errors.cxmlIncludeUnitOfMeasure}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.cxmlIncludeUnitOfMeasure'
                        )}
                      />

                      <Form.Label
                        htmlFor={'cxmlIncludeUnitOfMeasure'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlIncludeUnitOfMeasure'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('cxmlConvertUomToUppercase', {
                          disabled: isDisabled
                        })}
                        id={'cxmlConvertUomToUppercase'}
                        isInvalid={!!errors.cxmlConvertUomToUppercase}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.cxmlConvertUomToUppercase'
                        )}
                      />

                      <Form.Label
                        htmlFor={'cxmlConvertUomToUppercase'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlConvertUomToUppercase'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('outDoUnitConversion', {
                          disabled: isDisabled
                        })}
                        id={'outDoUnitConversion'}
                        isInvalid={!!errors.outDoUnitConversion}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.outDoUnitConversion'
                        )}
                      />

                      <Form.Label
                        htmlFor={'outDoUnitConversion'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.advancedSettings.tab.label.outDoUnitConversion'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('cxmlHeaderIncludeContactElement', {
                          disabled: isDisabled
                        })}
                        id={'cxmlHeaderIncludeContactElement'}
                        isInvalid={!!errors.cxmlHeaderIncludeContactElement}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.cxmlHeaderIncludeContactElement'
                        )}
                      />

                      <Form.Label
                        htmlFor={'cxmlHeaderIncludeContactElement'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlHeaderIncludeContactElement'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.cxmlHeaderContactRole'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('cxmlHeaderContactRole', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 30,
                            message: t('validation.error.string.max_length', {
                              count: 30
                            })
                          }
                        })}
                        isInvalid={!!errors.cxmlHeaderContactRole}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.cxmlHeaderContactRole'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.cxmlHeaderContactRole?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.contentTypeOverride'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('contentTypeOverride', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 100,
                            message: t('validation.error.string.max_length', {
                              count: 100
                            })
                          }
                        })}
                        isInvalid={!!errors.contentTypeOverride}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.contentTypeOverride'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.contentTypeOverride?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.advancedSettings.tab.label.sendingRank')}
                      </Form.Label>
                      <Form.Control
                        {...register('sendingRank', { disabled: isDisabled })}
                        type="number"
                        isInvalid={!!errors.sendingRank}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.sendingRank'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.sendingRank?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.advancedSettings.tab.label.endpointUrl')}
                      </Form.Label>
                      <Form.Control
                        {...register('endpointUrl', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 500,
                            message: t('validation.error.string.max_length', {
                              count: 500
                            })
                          }
                        })}
                        isInvalid={!!errors.endpointUrl}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.endpointUrl'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.endpointUrl?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <br />
                <br />
                <h4 className={styles['section-title']}>
                  {t('suppliers.advancedSettings.inbound_settings')}
                </h4>
                <hr />

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.advancedSettings.tab.label.inUsername')}
                      </Form.Label>
                      <Form.Control
                        {...register('inUsername', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 255,
                            message: t('validation.error.string.max_length', {
                              count: 255
                            })
                          }
                        })}
                        isInvalid={!!errors.inUsername}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.inUsername'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.inUsername?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t('suppliers.advancedSettings.tab.label.inPassword')}
                      </Form.Label>
                      <Form.Control
                        {...register('inPassword', {
                          disabled: isDisabled,
                          maxLength: {
                            value: 255,
                            message: t('validation.error.string.max_length', {
                              count: 255
                            })
                          }
                        })}
                        isInvalid={!!errors.inPassword}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.inPassword'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.inPassword?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('inConfirmUpdates', {
                          disabled: isDisabled
                        })}
                        id={'inConfirmUpdates'}
                        isInvalid={!!errors.inConfirmUpdates}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.inConfirmUpdates'
                        )}
                      />

                      <Form.Label
                        htmlFor={'inConfirmUpdates'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.advancedSettings.tab.label.inConfirmUpdates'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('inPriceCheck', { disabled: isDisabled })}
                        id={'inPriceCheck'}
                        isInvalid={!!errors.inPriceCheck}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.inPriceCheck'
                        )}
                      />

                      <Form.Label
                        htmlFor={'inPriceCheck'}
                        className={styles['cb-label']}
                      >
                        {t('suppliers.advancedSettings.tab.label.inPriceCheck')}
                      </Form.Label>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('inShippingNotesUpdates', {
                          disabled: isDisabled
                        })}
                        id={'inShippingNotesUpdates'}
                        isInvalid={!!errors.inShippingNotesUpdates}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.inShippingNotesUpdates'
                        )}
                      />
                      <Form.Label
                        htmlFor={'inShippingNotesUpdates'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.advancedSettings.tab.label.inShippingNotesUpdates'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('inIgnoreDeliveryDatesForAsn', {
                          disabled: isDisabled
                        })}
                        id={'inIgnoreDeliveryDatesForAsn'}
                        isInvalid={!!errors.inIgnoreDeliveryDatesForAsn}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.inIgnoreDeliveryDatesForAsn'
                        )}
                      />
                      <Form.Label
                        htmlFor={'inIgnoreDeliveryDatesForAsn'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.advancedSettings.tab.label.inIgnoreDeliveryDatesForAsn'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('inUpdateRejectedToZero', {
                          disabled: isDisabled
                        })}
                        id={'inUpdateRejectedToZero'}
                        isInvalid={!!errors.inUpdateRejectedToZero}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.inUpdateRejectedToZero'
                        )}
                      />

                      <Form.Label
                        htmlFor={'inUpdateRejectedToZero'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.advancedSettings.tab.label.inUpdateRejectedToZero'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Check
                        {...register('inDoUnitConversion', {
                          disabled: isDisabled
                        })}
                        id={'inDoUnitConversion'}
                        isInvalid={!!errors.inDoUnitConversion}
                        className={styles['cb-box']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.inDoUnitConversion'
                        )}
                      />
                      <Form.Label
                        htmlFor={'inDoUnitConversion'}
                        className={styles['cb-label']}
                      >
                        {t(
                          'suppliers.advancedSettings.tab.label.inDoUnitConversion'
                        )}
                      </Form.Label>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.inDeliveryDateConfirmationAdjustment'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('inDeliveryDateConfirmationAdjustment', {
                          disabled: isDisabled
                        })}
                        type="number"
                        min={0}
                        isInvalid={
                          !!errors.inDeliveryDateConfirmationAdjustment
                        }
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.inDeliveryDateConfirmationAdjustment'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.inDeliveryDateConfirmationAdjustment
                          ?.message ?? ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className={styles.group}>
                      <Form.Label>
                        {t(
                          'suppliers.advancedSettings.tab.label.inDeliveryDateShippingAdjustment'
                        )}
                      </Form.Label>
                      <Form.Control
                        {...register('inDeliveryDateShippingAdjustment', {
                          disabled: isDisabled
                        })}
                        type="number"
                        min={0}
                        isInvalid={!!errors.inDeliveryDateShippingAdjustment}
                        className={styles['form-control']}
                        aria-label={t(
                          'suppliers.advancedSettings.tab.label.inDeliveryDateShippingAdjustment'
                        )}
                      />

                      <Form.Control.Feedback type="invalid">
                        {errors?.inDeliveryDateShippingAdjustment?.message ??
                          ''}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </MaPanel.Body>
      </MaPanel>
    </div>
  )
}

export default AdvancedSettings
